import React from 'react';
import {
    Alert, Button, Card, Checkbox, Col, Input, Popconfirm, Row, Space,
    Table, Tag, Typography, message
} from 'antd';
import { CheckOutlined, CloseOutlined, HistoryOutlined, SearchOutlined } from '@ant-design/icons';
import { anonApi } from '../../util/anonymizerApi';

const { Text } = Typography;

/** client-side preview of the text with only the selected findings applied
 * (the server recomposes authoritatively on apply) */
function composePreview(original, findings, selected) {
    const spans = selected
        .map((i) => findings[i])
        .filter(Boolean)
        .sort((a, b) => b.start - a.start);
    let text = String(original === null || original === undefined ? '' : original);
    spans.forEach((f) => {
        text = text.slice(0, f.start) + '<' + f.type + '>' + text.slice(f.end);
    });
    return text;
}

/** old -> new diff for one field; findings (if present) are individually
 * toggleable - e.g. keep the agent's name but mask the email */
const FieldDiff = ({ name, diff, checked, onToggle, findingSel, onFindingToggle }) => {
    const findings = diff.findings || [];
    const hasFindings = findings.length > 0;
    const original = String(diff.old === null || diff.old === undefined ? '(empty)' : diff.old);
    const preview = hasFindings ? composePreview(diff.old, findings, findingSel) : String(diff.new);
    return (
        <div style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' }}>
            <Checkbox checked={checked} onChange={(e) => onToggle(e.target.checked)} style={{ marginTop: 2 }} />
            <div style={{ flex: 1, opacity: checked ? 1 : 0.45 }}>
                <Text strong style={{ fontSize: 13 }}>{name}</Text>
                <div style={{ fontSize: 13 }}>
                    <Text delete type="secondary">{original.slice(0, 200)}</Text>
                </div>
                {hasFindings && (
                    <div style={{ margin: '4px 0' }}>
                        {findings.map((f, i) => (
                            <Checkbox
                                key={i}
                                checked={findingSel.indexOf(i) !== -1}
                                disabled={!checked}
                                onChange={(e) => onFindingToggle(i, e.target.checked)}
                                style={{ marginRight: 10, fontSize: 12 }}
                            >
                                <Tag color="geekblue" style={{ marginRight: 4 }}>{f.type}</Tag>
                                <Text code style={{ fontSize: 12 }}>{String(diff.old || '').slice(f.start, f.end)}</Text>
                            </Checkbox>
                        ))}
                    </div>
                )}
                <div style={{ fontSize: 13 }}>
                    <Text type="success">{preview.slice(0, 250)}</Text>
                </div>
            </div>
        </div>
    );
};

const PendingRow = ({ entry, onDone }) => {
    const fieldNames = Object.keys(entry.fields || {});
    const [selected, setSelected] = React.useState(fieldNames);
    // per-field selected finding indices (default: all findings)
    const [findingSel, setFindingSel] = React.useState(() => {
        const init = {};
        fieldNames.forEach((f) => {
            init[f] = (entry.fields[f].findings || []).map((_, i) => i);
        });
        return init;
    });
    const [busy, setBusy] = React.useState(false);

    const apply = () => {
        // send finding selection only where the user deselected something
        const findings = {};
        selected.forEach((f) => {
            const all = (entry.fields[f].findings || []).length;
            if (all > 0 && findingSel[f].length < all) {
                findings[f] = findingSel[f];
            }
        });
        setBusy(true);
        anonApi('/v1/apply', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id: entry.id, fields: selected, findings })
        })
            .then(() => {
                message.success(`${entry.recordId}: ${selected.length} field(s) anonymized`);
                onDone();
            })
            .catch((e) => message.error('Apply failed: ' + e.message))
            .finally(() => setBusy(false));
    };

    const dismiss = () => {
        setBusy(true);
        anonApi(`/v1/pending/${entry.id}/dismiss`, { method: 'POST' })
            .then(() => {
                message.info(`${entry.recordId} dismissed - nothing changed`);
                onDone();
            })
            .catch((e) => message.error('Dismiss failed: ' + e.message))
            .finally(() => setBusy(false));
    };

    return (
        <div style={{ padding: '4px 0 12px' }}>
            {fieldNames.map((f) => (
                <FieldDiff
                    key={f}
                    name={f}
                    diff={entry.fields[f]}
                    checked={selected.indexOf(f) !== -1}
                    onToggle={(on) => setSelected(on ? [...selected, f] : selected.filter((x) => x !== f))}
                    findingSel={findingSel[f] || []}
                    onFindingToggle={(i, on) => setFindingSel({
                        ...findingSel,
                        [f]: on ? [...findingSel[f], i] : findingSel[f].filter((x) => x !== i)
                    })}
                />
            ))}
            <Space style={{ marginTop: 10 }}>
                <Popconfirm title={`Anonymize ${selected.length} field(s) of ${entry.recordId} in ${entry.form}?`} onConfirm={apply} okText="Apply">
                    <Button type="primary" icon={<CheckOutlined />} loading={busy} disabled={selected.length === 0}>
                        Apply ({selected.length}/{fieldNames.length} fields)
                    </Button>
                </Popconfirm>
                <Popconfirm title="Remove this proposal without changing the ticket?" onConfirm={dismiss}>
                    <Button icon={<CloseOutlined />} loading={busy}>Dismiss</Button>
                </Popconfirm>
            </Space>
        </div>
    );
};

/**
 * Service-desk review: pending anonymization proposals (from runs whose
 * entries are in "review" mode) with per-field approval, plus restore
 * search over the audit log (by ticket or record id).
 */
const ApprovalsPanel = () => {
    const [pending, setPending] = React.useState([]);
    const [auditQuery, setAuditQuery] = React.useState('');
    const [audit, setAudit] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const load = React.useCallback(() => {
        anonApi('/v1/pending', { method: 'GET' }).then(setPending).catch(() => setPending([]));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const searchAudit = () => {
        if (!auditQuery.trim()) { setAudit(null); return; }
        setLoading(true);
        anonApi(`/v1/audit?q=${encodeURIComponent(auditQuery.trim())}`, { method: 'GET' })
            .then(setAudit)
            .catch((e) => message.error('Search failed: ' + e.message))
            .finally(() => setLoading(false));
    };

    const restore = (entry) => {
        anonApi('/v1/restore', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id: entry.id })
        })
            .then((r) => {
                message.success(`${entry.recordId}: ${r.restored.length} field(s) restored`);
                searchAudit();
            })
            .catch((e) => message.error('Restore failed: ' + e.message));
    };

    return (
        <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={14}>
                <Card
                    size="small"
                    title={
                        <Space>
                            Pending Approvals
                            <Tag color={pending.length ? 'orange' : 'success'}>{pending.length}</Tag>
                        </Space>
                    }
                    extra={<Button size="small" onClick={load}>Refresh</Button>}
                >
                    {pending.length === 0 ? (
                        <Text type="secondary">No changes waiting for approval.</Text>
                    ) : (
                        <Table
                            size="small"
                            rowKey="id"
                            dataSource={pending}
                            pagination={{ pageSize: 8 }}
                            expandable={{ expandedRowRender: (entry) => <PendingRow entry={entry} onDone={load} /> }}
                            columns={[
                                { title: 'Ticket', dataIndex: 'ticket', width: 160, render: (v) => <b>{v || '—'}</b> },
                                { title: 'Record', dataIndex: 'recordId', width: 170 },
                                { title: 'Form', dataIndex: 'form', width: 140 },
                                { title: 'Fields', key: 'f', width: 70, render: (_, r) => Object.keys(r.fields || {}).length },
                                { title: 'Profile', dataIndex: 'profile', width: 100, render: (v) => v ? <Tag>{v}</Tag> : '—' },
                                { title: 'Proposed', dataIndex: 'createdAt', render: (v) => new Date(v).toLocaleString() }
                            ]}
                        />
                    )}
                </Card>
            </Col>
            <Col span={10}>
                <Card size="small" title={<Space><HistoryOutlined /> Applied Changes / Restore</Space>}>
                    <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
                        <Input
                            placeholder="Search ticket or record id, e.g. INC910000000001"
                            value={auditQuery}
                            onChange={(e) => setAuditQuery(e.target.value)}
                            onPressEnter={searchAudit}
                        />
                        <Button icon={<SearchOutlined />} onClick={searchAudit} loading={loading}>Search</Button>
                    </Space.Compact>
                    {audit && audit.length === 0 && <Alert type="info" showIcon message="No applied changes found for this search." />}
                    {audit && audit.length > 0 && (
                        <Table
                            size="small"
                            rowKey="id"
                            dataSource={audit}
                            pagination={{ pageSize: 6 }}
                            columns={[
                                { title: 'When', dataIndex: 'at', width: 150, render: (v) => new Date(v).toLocaleString() },
                                { title: 'Action', dataIndex: 'action', width: 80, render: (v) => <Tag color={v === 'apply' ? 'blue' : v === 'restore' ? 'green' : 'default'}>{v}</Tag> },
                                { title: 'Record', dataIndex: 'recordId', width: 160 },
                                {
                                    title: '', key: 'restore', width: 100,
                                    render: (_, r) => r.restorable ? (
                                        <Popconfirm title={`Restore the ORIGINAL values of ${r.recordId}? This writes PII back to the ticket.`} onConfirm={() => restore(r)} okText="Restore">
                                            <Button size="small" danger>Restore</Button>
                                        </Popconfirm>
                                    ) : null
                                }
                            ]}
                        />
                    )}
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        Originals are stored encrypted and purged after the retention period - after that,
                        restore is no longer possible.
                    </Text>
                </Card>
            </Col>
        </Row>
    );
};

export default ApprovalsPanel;
